import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, Check, Loader2, Package, Search, Star, Tags, X } from 'lucide-react';
import type {
    VisitReportCategoryPickerItem as ItemCategoryPickerItem,
    VisitReportProductPickerItem as ProductPickerItem,
} from '../../types/visitReport';

/**
 * *Dolder Alternative* as a picker instead of a text field (decision D1/D3).
 *
 * An entry is either one article from `etl.Product`, one leaf of the item-category tree, or a
 * piece of **legacy prose** that was already in the CRM. The list order is the order in
 * HubSpot, and position 0 is the primary.
 *
 * ── THE ONE THING NOT TO BREAK ─────────────────────────────────────────────────────────────
 *
 * A legacy value is **one** value. `"Sintal S 69/75 RM o Sintal S 408/75 RM"` and
 * `"Nubifer R-5580 Red oder R-5501, oder R-5530"` are real records, and both have to come back
 * out of a save byte for byte. Reading therefore splits on `|` and on nothing else — never on
 * " o ", " oder ", a comma or a space. We write cleaner values; we do not reformat somebody's
 * wording, and we certainly do not invent two products out of one.
 *
 * The preview underneath is not decoration: it is the exact string that will be sent, and Save
 * stays disabled while it does not fit. The server checks the same cap again and refuses with
 * the real length — this side just makes the refusal unnecessary.
 */

/** Mirrors shared/marketdata-alternatives.ts. The server is authoritative and re-checks both. */
const ALTERNATIVE_SEPARATOR = ' | ';
export const ALTERNATIVE_MAX = 255;

export interface AlternativeEntry {
    /** Stable React key. Not sent anywhere. */
    key: string;
    kind: 'product' | 'subclass' | 'legacy';
    productId?: string;
    itemCategoryId?: string;
    /** Exactly what goes into HubSpot for this entry. */
    displayText: string;
    /** Full ERP name / parent category — context in the list, never written. */
    subtitle?: string;
    companyCode?: string;
    /** Blocked in the ERP since it was linked. The search never offers these. */
    isBlocked?: boolean;
}

let keySeq = 0;
const nextKey = () => `alt-${++keySeq}`;

/** The exact string that goes to HubSpot, in list order. */
export function buildAlternativeValue(entries: AlternativeEntry[]): string {
    return entries.map(e => e.displayText.trim()).filter(Boolean).join(ALTERNATIVE_SEPARATOR);
}

/**
 * Where the two typeaheads get their hits.
 *
 * Injectable because two pages offered this picker: the Market Intelligence tab of Customer 360
 * and the market-data section of the visit report (P4). Each reached the one server-side
 * implementation (`api/src/shared/alternative-search.ts`) through its own route, because the
 * permission guard is per page. Since P9 (2026-09-21) only the visit report is left here, and
 * the injection point stays: it is what kept the two apart, and there is no default any more —
 * a caller has to say which routes it is entitled to.
 */
export interface AlternativeSearchSource {
    products: (companyCode: string, q: string) => Promise<ProductPickerItem[]>;
    categories: (companyCode: string, q: string) => Promise<ItemCategoryPickerItem[]>;
}

interface Props {
    /** ERP company of the record — the default side of the catalogue and the reference for
     *  the "other company" marker. */
    recordCompanyCode: string;
    /** Every ERP company that can be picked from. Cross-company is allowed (F3). */
    companies: string[];
    entries: AlternativeEntry[];
    onChange: (entries: AlternativeEntry[]) => void;
    disabled?: boolean;
    /** Reported by the caller: links that no longer match the text in the CRM. */
    staleLinks?: string[];
    /** Required — see `AlternativeSearchSource`. */
    search: AlternativeSearchSource;
}

type Mode = 'product' | 'subclass';

export function DolderAlternativePicker({
    recordCompanyCode, companies, entries, onChange, disabled = false, staleLinks = [],
    search: searchSource,
}: Props) {
    const [mode, setMode] = useState<Mode>('product');
    const [companyCode, setCompanyCode] = useState(recordCompanyCode);
    const [search, setSearch] = useState('');
    const [products, setProducts] = useState<ProductPickerItem[]>([]);
    const [categories, setCategories] = useState<ItemCategoryPickerItem[]>([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const requestSeq = useRef(0);

    // Debounced typeahead. The sequence number is what stops a slow early request from
    // overwriting the results of a fast later one — the classic reason a picker shows hits
    // for a term the user has already finished typing over.
    useEffect(() => {
        if (disabled || !companyCode) return;
        const seq = ++requestSeq.current;
        const timer = setTimeout(async () => {
            setSearching(true);
            setSearchError(null);
            try {
                if (mode === 'product') {
                    const items = await searchSource.products(companyCode, search.trim());
                    if (seq === requestSeq.current) setProducts(items);
                } else {
                    const items = await searchSource.categories(companyCode, search.trim());
                    if (seq === requestSeq.current) setCategories(items);
                }
            } catch (err) {
                if (seq === requestSeq.current) {
                    setSearchError(err instanceof Error ? err.message : String(err));
                }
            } finally {
                if (seq === requestSeq.current) setSearching(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [mode, companyCode, search, disabled, searchSource]);

    const takenProductIds = useMemo(
        () => new Set(entries.filter(e => e.productId).map(e => e.productId!.toUpperCase())),
        [entries],
    );
    const takenCategoryIds = useMemo(
        () => new Set(entries.filter(e => e.itemCategoryId).map(e => e.itemCategoryId!.toUpperCase())),
        [entries],
    );

    const preview = buildAlternativeValue(entries);
    const tooLong = preview.length > ALTERNATIVE_MAX;

    // Two different articles can carry the same name once the pack size is stripped —
    // "EPILOX P 13-16 - Fusti 170 kg" and "… 200 kg" are a real pair. Written together the
    // value reads "X | X" and nobody can tell afterwards which two were meant, so the server
    // refuses it. Say so here instead of letting the save fail.
    const duplicateText = useMemo(() => {
        const seen = new Set<string>();
        for (const e of entries) {
            const key = e.displayText.trim().toUpperCase();
            if (!key) continue;
            if (seen.has(key)) return e.displayText;
            seen.add(key);
        }
        return null;
    }, [entries]);

    const withPipe = entries.find(e => e.displayText.includes('|'));

    const add = (entry: AlternativeEntry) => {
        onChange([...entries, entry]);
        setSearch('');
    };
    const remove = (key: string) => onChange(entries.filter(e => e.key !== key));
    const move = (index: number, delta: number) => {
        const target = index + delta;
        if (target < 0 || target >= entries.length) return;
        const next = [...entries];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };
    const makePrimary = (index: number) => {
        if (index === 0) return;
        const next = [...entries];
        const [entry] = next.splice(index, 1);
        onChange([entry, ...next]);
    };

    return (
        <div className="space-y-3">
            {staleLinks.length > 0 && (
                <Note tone="warning">
                    {staleLinks.length === 1
                        ? `One link ("${staleLinks[0]}") no longer appears in the value that stands in HubSpot — `
                        : `${staleLinks.length} links no longer appear in the value that stands in HubSpot — `}
                    the field was edited in the CRM. The text below is what the CRM holds; saving
                    replaces the links to match it.
                </Note>
            )}

            {/* --- The current list, in the order it will be written --- */}
            {entries.length === 0 ? (
                <p className="rounded-md border border-dashed border-gray-300 px-3 py-4 text-center text-xs text-gray-400">
                    No alternative yet. Search our own catalogue below, or leave it empty.
                </p>
            ) : (
                <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                    {entries.map((entry, index) => {
                        const foreign = !!entry.companyCode
                            && entry.companyCode.toLowerCase() !== recordCompanyCode.toLowerCase();
                        return (
                            <li key={entry.key} className="flex items-start gap-2 px-2.5 py-2">
                                <button
                                    type="button"
                                    disabled={disabled || index === 0}
                                    onClick={() => makePrimary(index)}
                                    title={index === 0 ? 'Primary — leads the value in HubSpot' : 'Make this the primary'}
                                    className={`mt-0.5 rounded p-0.5 ${index === 0
                                        ? 'text-amber-500'
                                        : 'text-gray-300 hover:bg-gray-100 hover:text-amber-500 disabled:opacity-40'}`}
                                >
                                    <Star className={`h-3.5 w-3.5 ${index === 0 ? 'fill-amber-400' : ''}`} />
                                </button>

                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <span className="truncate text-sm text-gray-900">{entry.displayText}</span>
                                        <KindChip kind={entry.kind} />
                                        {foreign && (
                                            <span
                                                className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700"
                                                title={`From ${entry.companyCode?.toUpperCase()}, while this record belongs to ${recordCompanyCode.toUpperCase()}. Allowed — several Dolder companies serve the same customer.`}
                                            >
                                                {entry.companyCode}
                                            </span>
                                        )}
                                        {entry.isBlocked && (
                                            <span
                                                className="rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-red-700"
                                                title="This article is blocked in the ERP. It was linked before that happened; the search no longer offers it."
                                            >
                                                blocked
                                            </span>
                                        )}
                                    </div>
                                    {entry.subtitle && entry.subtitle !== entry.displayText && (
                                        <p className="truncate text-[11px] text-gray-400">{entry.subtitle}</p>
                                    )}
                                    {entry.kind === 'legacy' && (
                                        <p className="text-[11px] text-gray-400">
                                            Free text from the CRM — kept exactly as it is, linked to nothing.
                                        </p>
                                    )}
                                </div>

                                <div className="flex shrink-0 items-center gap-0.5">
                                    <IconButton
                                        label="Move up" disabled={disabled || index === 0}
                                        onClick={() => move(index, -1)}
                                    ><ArrowUp className="h-3.5 w-3.5" /></IconButton>
                                    <IconButton
                                        label="Move down" disabled={disabled || index === entries.length - 1}
                                        onClick={() => move(index, 1)}
                                    ><ArrowDown className="h-3.5 w-3.5" /></IconButton>
                                    <IconButton
                                        label={`Remove ${entry.displayText}`} disabled={disabled} danger
                                        onClick={() => remove(entry.key)}
                                    ><X className="h-3.5 w-3.5" /></IconButton>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* --- Search our own catalogue --- */}
            <div className="rounded-md border border-gray-200">
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
                    <div className="inline-flex overflow-hidden rounded-md border border-gray-300 bg-white">
                        <ModeButton active={mode === 'product'} onClick={() => setMode('product')} disabled={disabled}>
                            <Package className="h-3.5 w-3.5" /> Article
                        </ModeButton>
                        <ModeButton active={mode === 'subclass'} onClick={() => setMode('subclass')} disabled={disabled}>
                            <Tags className="h-3.5 w-3.5" /> Subclass
                        </ModeButton>
                    </div>

                    {/* Cross-company is allowed (F3): 21 of 2'470 companies already carry market
                        data for more than one Dolder entity, 13 of them bin+dbi. Blocking it
                        would tear those apart. A foreign entry is marked, not refused. */}
                    <label className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-500">
                        Catalogue
                        <select
                            value={companyCode}
                            disabled={disabled}
                            onChange={e => setCompanyCode(e.target.value)}
                            className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs uppercase"
                        >
                            {companies.map(c => (
                                <option key={c} value={c}>
                                    {c.toUpperCase()}{c.toLowerCase() === recordCompanyCode.toLowerCase() ? ' · this record' : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className="relative border-b border-gray-100 p-2">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        disabled={disabled}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={mode === 'product' ? 'Search our articles…' : 'Search subclasses…'}
                        className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-8 text-sm focus:border-blue-300 focus:outline-none"
                    />
                    {searching && <Loader2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-300" />}
                </div>

                {searchError ? (
                    <p className="px-3 py-4 text-center text-xs text-red-600">{searchError}</p>
                ) : (
                    <ul className="max-h-48 overflow-y-auto py-1">
                        {mode === 'product' ? (
                            products.length === 0 ? (
                                <Empty>
                                    {searching ? 'Searching…' : 'Nothing in our catalogue matches.'}
                                </Empty>
                            ) : products.map(p => {
                                const taken = takenProductIds.has(p.productId.toUpperCase());
                                return (
                                    <ResultRow
                                        key={p.productId}
                                        taken={taken}
                                        disabled={disabled}
                                        title={p.displayText}
                                        subtitle={p.productName}
                                        onAdd={() => add({
                                            key: nextKey(), kind: 'product', productId: p.productId,
                                            displayText: p.displayText, subtitle: p.productName,
                                            companyCode: p.companyCode, isBlocked: false,
                                        })}
                                    />
                                );
                            })
                        ) : (
                            categories.length === 0 ? (
                                <Empty>{searching ? 'Searching…' : 'No subclass matches.'}</Empty>
                            ) : categories.map(c => {
                                const taken = takenCategoryIds.has(c.itemCategoryId.toUpperCase());
                                return (
                                    <ResultRow
                                        key={c.itemCategoryId}
                                        taken={taken}
                                        disabled={disabled}
                                        title={c.categoryName}
                                        subtitle={[c.parentCategoryName, c.baseCategoryName].filter(Boolean).join(' · ')
                                            || c.categoryCode}
                                        onAdd={() => add({
                                            key: nextKey(), kind: 'subclass', itemCategoryId: c.itemCategoryId,
                                            displayText: c.displayText,
                                            subtitle: [c.parentCategoryName, c.baseCategoryName].filter(Boolean).join(' · '),
                                            companyCode: c.companyCode,
                                        })}
                                    />
                                );
                            })
                        )}
                    </ul>
                )}

                {mode === 'product' && (
                    <p className="border-t border-gray-100 px-3 py-1.5 text-[11px] text-gray-400">
                        Blocked articles are not offered — an alternative we cannot deliver is not one.
                    </p>
                )}
            </div>

            {/* --- The exact string that goes to HubSpot --- */}
            <div>
                <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium text-gray-600">Goes to HubSpot as</span>
                    <span className={`text-[11px] tabular-nums ${tooLong ? 'font-semibold text-red-600' : 'text-gray-400'}`}>
                        {preview.length} / {ALTERNATIVE_MAX}
                    </span>
                </div>
                <p className={`min-h-[2.25rem] whitespace-pre-wrap break-words rounded-md border px-2.5 py-1.5 font-mono text-xs ${
                    tooLong ? 'border-red-300 bg-red-50 text-red-800' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                    {preview || <span className="font-sans italic text-gray-400">empty — the field will be cleared</span>}
                </p>

                {tooLong && (
                    <Note tone="error">
                        {preview.length} characters, and the HubSpot field holds {ALTERNATIVE_MAX}. Remove an
                        entry — nothing is shortened, because a shortened product name is a wrong product name.
                    </Note>
                )}
                {duplicateText && (
                    <Note tone="error">
                        Two entries would both be written as “{duplicateText}”. Remove one — otherwise the
                        value cannot be read back into two articles.
                    </Note>
                )}
                {withPipe && (
                    <Note tone="error">
                        “{withPipe.displayText}” contains a “|”, which is what separates two alternatives in
                        this field. It cannot be written without making the value ambiguous.
                    </Note>
                )}
            </div>
        </div>
    );
}


// --- Small building blocks ---------------------------------------------------

function KindChip({ kind }: { kind: AlternativeEntry['kind'] }) {
    const style = kind === 'product'
        ? 'border-blue-200 bg-blue-50 text-blue-700'
        : kind === 'subclass'
            ? 'border-violet-200 bg-violet-50 text-violet-700'
            : 'border-gray-200 bg-gray-50 text-gray-500';
    const label = kind === 'product' ? 'article' : kind === 'subclass' ? 'subclass' : 'free text';
    return (
        <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase ${style}`}>
            {label}
        </span>
    );
}

function IconButton({ label, onClick, disabled, danger, children }: {
    label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
            className={`rounded p-1 text-gray-400 disabled:opacity-30 ${
                danger ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-gray-100 hover:text-gray-700'}`}
        >
            {children}
        </button>
    );
}

function ModeButton({ active, onClick, disabled, children }: {
    active: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium disabled:opacity-40 ${
                active ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
        >
            {children}
        </button>
    );
}

function ResultRow({ title, subtitle, taken, disabled, onAdd }: {
    title: string; subtitle?: string; taken: boolean; disabled?: boolean; onAdd: () => void;
}) {
    return (
        <li>
            <button
                type="button"
                onClick={onAdd}
                disabled={disabled || taken}
                className="flex w-full items-start gap-2 px-3 py-1.5 text-left hover:bg-blue-50 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
            >
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    {taken && <Check className="h-3.5 w-3.5 text-green-600" />}
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-gray-800">{title}</span>
                    {subtitle && subtitle !== title && (
                        <span className="block truncate text-[11px] text-gray-400">{subtitle}</span>
                    )}
                </span>
            </button>
        </li>
    );
}

function Empty({ children }: { children: React.ReactNode }) {
    return <li className="px-3 py-6 text-center text-xs text-gray-400">{children}</li>;
}

function Note({ tone, children }: { tone: 'warning' | 'error'; children: React.ReactNode }) {
    const style = tone === 'error'
        ? 'border-red-200 bg-red-50 text-red-700'
        : 'border-amber-200 bg-amber-50 text-amber-800';
    return (
        <div className={`mt-1.5 flex items-start gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] ${style}`}>
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="min-w-0">{children}</span>
        </div>
    );
}
